import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { stripCodeSystem } from '@features/encounters/utils/billingClaim';
import { Add, AutoAwesome, InfoOutlined, Remove } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type {
  WorkingBillingClaimLineItemResponse4,
  WorkingClaimLineItem,
} from '@store/generated/encounters';

/**
 * CPT Code option with metadata
 */
export interface CPTCodeOption {
  cpt: string;
  description: string;
  category:
    | 'Evaluation'
    | 'Psychotherapy'
    | 'Add-on'
    | 'Crisis'
    | 'Medication Management'
    | 'Other';
}

/**
 * Common CPT codes for psychiatric/behavioral health encounters
 * Ordered by category: Medication Management, Evaluation, Psychotherapy, Crisis, Other, Add-on
 */
export const CPT_CODES: CPTCodeOption[] = [
  // Medication Management (E/M codes)
  {
    cpt: '99212',
    description: 'E/M established patient, low complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99213',
    description: 'E/M established patient, moderate complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99214',
    description: 'E/M established patient, high complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99215',
    description: 'E/M established patient, highest complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99203',
    description: 'E/M new patient, moderate complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99204',
    description: 'E/M new patient, high complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99205',
    description: 'E/M new patient, highest complexity',
    category: 'Medication Management',
  },
  {
    cpt: '99211',
    description: 'E/M established patient, minimal',
    category: 'Medication Management',
  },

  // Evaluation
  {
    cpt: '90791',
    description: 'Psychiatric diagnostic evaluation (no medical services)',
    category: 'Evaluation',
  },
  {
    cpt: '90792',
    description: 'Psychiatric diagnostic evaluation with medical services',
    category: 'Evaluation',
  },

  // Psychotherapy
  {
    cpt: '90832',
    description: 'Psychotherapy, 30 minutes (individual)',
    category: 'Psychotherapy',
  },
  {
    cpt: '90834',
    description: 'Psychotherapy, 45 minutes (individual)',
    category: 'Psychotherapy',
  },
  {
    cpt: '90837',
    description: 'Psychotherapy, 60 minutes (individual)',
    category: 'Psychotherapy',
  },
  {
    cpt: '90846',
    description: 'Family or couples psychotherapy, without patient present',
    category: 'Psychotherapy',
  },
  {
    cpt: '90847',
    description: 'Family or couples psychotherapy, with patient present',
    category: 'Psychotherapy',
  },
  { cpt: '90853', description: 'Group psychotherapy', category: 'Psychotherapy' },
  { cpt: '90845', description: 'Psychoanalysis', category: 'Psychotherapy' },

  // Crisis
  { cpt: '90839', description: 'Psychotherapy for crisis, first 60 minutes', category: 'Crisis' },

  // Other
  { cpt: '90865', description: 'Narcosynthesis', category: 'Other' },

  // Add-on codes
  { cpt: '90833', description: 'Psychotherapy, 30 minutes (add-on with E/M)', category: 'Add-on' },
  { cpt: '90836', description: 'Psychotherapy, 45 minutes (add-on with E/M)', category: 'Add-on' },
  { cpt: '90838', description: 'Psychotherapy, 60 minutes (add-on with E/M)', category: 'Add-on' },
  {
    cpt: '90840',
    description: 'Psychotherapy for crisis, additional 30 minutes (add-on)',
    category: 'Add-on',
  },
  { cpt: '90785', description: 'Interactive complexity (add-on)', category: 'Add-on' },
  { cpt: '90882', description: 'Environmental intervention (add-on)', category: 'Add-on' },
  {
    cpt: '90887',
    description:
      'Interpretation or explanation of psychiatric/medical data to family or caregivers (add-on)',
    category: 'Add-on',
  },
];

// Primary codes (everything except add-ons)
const PRIMARY_CPT_CODES = CPT_CODES.filter(code => code.category !== 'Add-on');

// Add-on codes only
const ADDON_CPT_CODES = CPT_CODES.filter(code => code.category === 'Add-on');

/**
 * Common billing modifiers for psychiatric/behavioral health
 */
export interface ModifierOption {
  code: string;
  description: string;
}

export const COMMON_MODIFIERS: ModifierOption[] = [
  { code: '25', description: 'Significant, separately identifiable E/M service' },
  { code: '95', description: 'Synchronous telemedicine service' },
  { code: 'GT', description: 'Via interactive audio/video telecomm systems' },
  { code: '59', description: 'Distinct procedural service' },
  { code: '76', description: 'Repeat procedure by same physician' },
  { code: '77', description: 'Repeat procedure by another physician' },
  { code: 'HO', description: 'Masters degree level' },
  { code: 'XE', description: 'Separate encounter' },
  { code: 'XS', description: 'Separate structure' },
  { code: 'XP', description: 'Separate practitioner' },
  { code: 'XU', description: 'Unusual non-overlapping service' },
];

const MODIFIER_CODES = COMMON_MODIFIERS.map(m => m.code);

/**
 * Clean CPT codes: strip non-numeric characters, limit to 5 digits, filter empty
 * @param codes - Array of CPT code strings
 * @returns Cleaned array of CPT codes
 */
export const cleanCptCodes = (codes: string[]): string[] => {
  return codes
    .map(code => (code ? code.replace(/\D/g, '').slice(0, 5) : ''))
    .filter(code => code !== '');
};

export interface CPTCodePickerProps {
  /**
   * Array of CPT codes (max 2 codes)
   * @example ['99213', '99214']
   */
  value?: string[];
  /**
   * Callback when codes change
   * @param codes - Array of CPT codes (max 2 elements)
   */
  onChange: (codes: string[]) => void;
  /**
   * Optional callback when Enter key is pressed in any field
   */
  onEnter?: () => void;
  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
  /**
   * Whether to show the header with description and info tooltip
   * @default true
   */
  showHeader?: boolean;
  /**
   * Size of the TextField inputs
   * @default 'small'
   */
  size?: 'small' | 'medium';
  /**
   * AI-suggested CPT codes to display with a "Use Suggested Codes" button
   */
  suggestedCodes?: string[];
  /**
   * Callback when user clicks "Use Suggested Codes" button
   */
  onUseSuggestedCodes?: () => void;
  /**
   * Rich line items from working_billing_claim.
   * When provided, display codes are derived from these items (stripping code system prefix).
   */
  lineItems?: WorkingBillingClaimLineItemResponse4[];
  /**
   * Callback when line items change. Fires alongside onChange with rich metadata preserved.
   */
  onLineItemsChange?: (items: WorkingClaimLineItem[]) => void;
  /**
   * Whether to show inline modifier editing below each CPT code field.
   * @default false
   */
  showModifiers?: boolean;
  /**
   * Whether to show the helper text below the inputs
   * @default true
   */
  showHelperText?: boolean;
  /**
   * Claim-level ICD-10 diagnosis codes available for per-line linking.
   * When provided, the expansion area shows toggleable diagnosis chips.
   */
  claimDiagnosisCodes?: string[];
}

const CPT_INFO_TOOLTIP =
  "We'll use these CPT codes when writing the medical decision making. If you don't select a code, we'll assume one based on the content and complexity of the encounter.";

/**
 * Shared CPT Code Picker component with autocomplete
 *
 * Features:
 * - Autocomplete with common psychiatric CPT codes
 * - First field: Primary codes (evaluation, psychotherapy, E/M)
 * - Second field: Add-on codes only
 * - freeSolo mode allows custom codes
 * - Grouped by category for easy browsing
 *
 * @example
 * ```tsx
 * <CPTCodePicker
 *   value={cptCodes}
 *   onChange={(codes) => setCptCodes(codes)}
 *   onEnter={() => handleSave()}
 * />
 * ```
 */
export const CPTCodePicker: React.FC<CPTCodePickerProps> = ({
  value = [],
  onChange,
  onEnter,
  disabled = false,
  showHeader = true,
  size = 'small',
  suggestedCodes,
  onUseSuggestedCodes,
  lineItems,
  onLineItemsChange,
  showModifiers = false,
  showHelperText = true,
  claimDiagnosisCodes = [],
}) => {
  const theme = useTheme();

  // When lineItems are provided, derive display codes from them
  const derivedValue = useMemo(() => {
    if (lineItems) {
      return lineItems.map(li => (li.code_id ? stripCodeSystem(li.code_id) : ''));
    }
    return value;
  }, [lineItems, value]);

  // Ensure we have at least 2 slots and strip non-numeric characters
  const codes = useMemo(() => {
    const c = derivedValue.map(code => (code ? code.replace(/\D/g, '') : ''));
    while (c.length < 2) c.push('');
    return c;
  }, [derivedValue]);

  // Helper: build line items from bare codes, preserving metadata from existing items
  const buildLineItems = useCallback(
    (bareCodes: string[]): WorkingClaimLineItem[] => {
      return bareCodes.filter(Boolean).map(bareCode => {
        const existing = lineItems?.find(
          li => li.code_id && stripCodeSystem(li.code_id) === bareCode,
        );
        if (existing) {
          return {
            code_id: existing.code_id ?? `CPT:${bareCode}`,
            source: existing.source ?? 'provider_selected',
            modifiers: existing.modifiers,
            modifier_sources: existing.modifier_sources,
            units: existing.units ?? undefined,
            charge_cents: existing.charge_cents ?? undefined,
            diagnosis_codes: existing.diagnosis_codes,
          };
        }
        return {
          code_id: `CPT:${bareCode}`,
          source: 'provider_selected' as const,
        };
      });
    },
    [lineItems],
  );

  // Track which code slot (if any) has its "Modifiers & Units" area expanded (only one at a time)
  const [expandedSlot, setExpandedSlot] = useState<number | null>(null);

  const toggleSlotExpanded = useCallback((index: number) => {
    setExpandedSlot(prev => (prev === index ? null : index));
  }, []);

  // Local input state for controlled typing - allows user to type freely
  // Values are committed to parent onChange when option is selected, Enter is pressed, or on blur
  const [primaryInput, setPrimaryInput] = useState(codes[0] || '');
  const [addonInput, setAddonInput] = useState(codes[1] || '');

  // Extract values for dependency arrays
  const primaryCode = codes[0];
  const addonCode = codes[1];

  // Sync local input state when parent value changes (e.g., from "Use Suggested Codes")
  useEffect(() => {
    setPrimaryInput(primaryCode || '');
  }, [primaryCode]);

  useEffect(() => {
    setAddonInput(addonCode || '');
  }, [addonCode]);

  // Find selected options for controlled autocomplete
  const primaryValue = useMemo(
    () => PRIMARY_CPT_CODES.find(opt => opt.cpt === codes[0]) || null,
    [codes],
  );

  const addonValue = useMemo(
    () => ADDON_CPT_CODES.find(opt => opt.cpt === codes[1]) || null,
    [codes],
  );

  // Emit both onChange (bare strings) and onLineItemsChange (rich items) when codes change
  const emitChange = useCallback(
    (newCodes: string[]) => {
      const cleaned = cleanCptCodes(newCodes);
      onChange(cleaned);
      if (onLineItemsChange) {
        onLineItemsChange(buildLineItems(cleaned));
      }
    },
    [onChange, onLineItemsChange, buildLineItems],
  );

  const handlePrimaryChange = useCallback(
    (_event: React.SyntheticEvent, newValue: CPTCodeOption | string | null) => {
      let cptCode = '';
      if (typeof newValue === 'string') {
        // freeSolo: user typed a custom value
        cptCode = newValue.replace(/\D/g, '').slice(0, 5);
      } else if (newValue) {
        cptCode = newValue.cpt;
      }
      const newCodes = [...codes];
      newCodes[0] = cptCode;
      emitChange(newCodes);
    },
    [codes, emitChange],
  );

  const handleAddonChange = useCallback(
    (_event: React.SyntheticEvent, newValue: CPTCodeOption | string | null) => {
      let cptCode = '';
      if (typeof newValue === 'string') {
        // freeSolo: user typed a custom value
        cptCode = newValue.replace(/\D/g, '').slice(0, 5);
      } else if (newValue) {
        cptCode = newValue.cpt;
      }
      const newCodes = [...codes];
      newCodes[1] = cptCode;
      emitChange(newCodes);
    },
    [codes, emitChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && onEnter) {
        // Only trigger if dropdown is closed (user is confirming, not selecting)
        const target = e.target as HTMLInputElement;
        const isDropdownOpen = target.getAttribute('aria-expanded') === 'true';
        if (!isDropdownOpen) {
          e.preventDefault();
          onEnter();
        }
      }
    },
    [onEnter],
  );

  // Custom option rendering
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
    option: CPTCodeOption,
  ) => {
    const { key, ...otherProps } = props;
    return (
      <li key={key} {...otherProps}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {option.cpt}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {option.description}
          </Typography>
        </Box>
      </li>
    );
  };

  // Convert a response line item to a request line item, preserving all metadata
  const toRequestLineItem = useCallback(
    (li: WorkingBillingClaimLineItemResponse4): WorkingClaimLineItem => ({
      code_id: li.code_id ?? '',
      source: li.source ?? 'provider_selected',
      modifiers: li.modifiers,
      modifier_sources: li.modifier_sources,
      units: li.units ?? undefined,
      charge_cents: li.charge_cents ?? undefined,
      diagnosis_codes: li.diagnosis_codes,
    }),
    [],
  );

  // Handle modifier changes for a specific code slot
  const handleModifiersChange = useCallback(
    (codeIndex: number, newModifiers: string[]) => {
      if (!lineItems || !onLineItemsChange) return;

      const updated: WorkingClaimLineItem[] = lineItems.map((li, idx) => {
        if (idx !== codeIndex) return toRequestLineItem(li);

        const existingMods = new Set(li.modifiers ?? []);
        const newModSources: Record<string, 'provider_selected' | 'ai_recommended'> = {};

        for (const mod of newModifiers) {
          if (existingMods.has(mod) && li.modifier_sources?.[mod]) {
            newModSources[mod] = li.modifier_sources[mod];
          } else {
            newModSources[mod] = 'provider_selected';
          }
        }

        return {
          ...toRequestLineItem(li),
          modifiers: newModifiers,
          modifier_sources: newModSources,
        };
      });

      onLineItemsChange(updated);
    },
    [lineItems, onLineItemsChange, toRequestLineItem],
  );

  // Handle units changes for a specific code slot
  const handleUnitsChange = useCallback(
    (codeIndex: number, newUnits: number) => {
      if (!lineItems || !onLineItemsChange) return;

      const clamped = Math.max(1, Math.min(99, newUnits));

      const updated: WorkingClaimLineItem[] = lineItems.map((li, idx) => {
        const base = toRequestLineItem(li);
        if (idx !== codeIndex) return base;
        return { ...base, units: clamped };
      });

      onLineItemsChange(updated);
    },
    [lineItems, onLineItemsChange, toRequestLineItem],
  );

  // Handle diagnosis code pointer changes for a specific code slot
  const handleDiagnosisCodesChange = useCallback(
    (codeIndex: number, newDiagnosisCodes: string[]) => {
      if (!lineItems || !onLineItemsChange) return;

      const updated: WorkingClaimLineItem[] = lineItems.map((li, idx) => {
        const base = toRequestLineItem(li);
        if (idx !== codeIndex) return base;
        return { ...base, diagnosis_codes: newDiagnosisCodes };
      });

      onLineItemsChange(updated);
    },
    [lineItems, onLineItemsChange, toRequestLineItem],
  );

  // Render the toggle link for details under each code slot
  const renderModifiersToggle = useCallback(
    (index: number) => {
      if (!showModifiers || !codes[index]) return null;

      const isExpanded = expandedSlot === index;
      const currentModifiers = lineItems?.[index]?.modifiers ?? [];
      const currentUnits = lineItems?.[index]?.units ?? 1;

      const hasMods = currentModifiers.length > 0;
      const hasNonDefaultUnits = currentUnits !== 1;
      const isAiRecommended = lineItems?.[index]?.source === 'ai_recommended';
      return (
        <Box sx={{ mt: 0.5 }}>
          <Typography
            component="button"
            variant="caption"
            onClick={() => toggleSlotExpanded(index)}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: isExpanded ? 'primary.main' : 'text.secondary',
              fontSize: '0.7rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { color: 'text.primary' },
            }}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for code ${codes[index]}`}
          >
            {isExpanded ? '\u25BE' : '\u25B8'} Details
            {isAiRecommended && (
              <Tooltip title="JotPsych suggested" placement="top" arrow>
                <AutoAwesome sx={{ fontSize: '0.7rem', color: theme.palette.secondary.main }} />
              </Tooltip>
            )}
            {!isExpanded && (hasMods || hasNonDefaultUnits) && (
              <Chip
                label={[
                  hasMods ? currentModifiers.join(', ') : null,
                  hasNonDefaultUnits ? `${currentUnits}u` : null,
                ]
                  .filter(Boolean)
                  .join(' \u00B7 ')}
                size="small"
                sx={{
                  height: 16,
                  fontSize: '0.6rem',
                  fontFamily: 'monospace',
                  ml: 0.5,
                }}
              />
            )}
          </Typography>
        </Box>
      );
    },
    [showModifiers, codes, expandedSlot, toggleSlotExpanded, lineItems, theme],
  );

  // Render the full-width expanded content for the active slot
  const renderExpandedModifiersAndUnits = useCallback(() => {
    if (expandedSlot === null || !showModifiers || !codes[expandedSlot]) return null;

    const index = expandedSlot;
    const currentModifiers = lineItems?.[index]?.modifiers ?? [];
    const currentUnits = lineItems?.[index]?.units ?? 1;
    const codeLabel = codes[index];

    return (
      <Collapse in>
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Code label chip */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              label={codeLabel}
              size="small"
              color="primary"
              variant="outlined"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Modifiers & Units
            </Typography>
          </Box>

          <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
            {/* Modifier Autocomplete */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Autocomplete
                multiple
                freeSolo
                size="small"
                value={currentModifiers}
                options={MODIFIER_CODES}
                onChange={(_e, newValue) => handleModifiersChange(index, newValue as string[])}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((mod, i) => {
                    const { key, ...tagProps } = getTagProps({ index: i });
                    const modSource = lineItems?.[index]?.modifier_sources?.[mod];
                    return (
                      <Chip
                        key={key}
                        {...tagProps}
                        label={mod}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.7rem',
                          fontFamily: 'monospace',
                          fontWeight: 500,
                          borderLeft: modSource
                            ? `2px solid ${
                                modSource === 'ai_recommended'
                                  ? theme.palette.secondary.main
                                  : theme.palette.primary.main
                              }`
                            : undefined,
                        }}
                      />
                    );
                  })
                }
                renderOption={(props, option) => {
                  const { key, ...otherProps } = props as React.HTMLAttributes<HTMLLIElement> & {
                    key?: React.Key;
                  };
                  const modOption = COMMON_MODIFIERS.find(m => m.code === option);
                  return (
                    <li key={key} {...otherProps}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {option}
                        </Typography>
                        {modOption && (
                          <Typography variant="caption" color="text.secondary">
                            {modOption.description}
                          </Typography>
                        )}
                      </Box>
                    </li>
                  );
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Modifiers"
                    variant="standard"
                    sx={{
                      '& .MuiInput-underline:before': { borderBottomStyle: 'dashed' },
                    }}
                    inputProps={{ ...params.inputProps, maxLength: 2 }}
                  />
                )}
                disabled={disabled}
              />
            </Box>

            {/* Units stepper */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.5, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Units
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleUnitsChange(index, currentUnits - 1)}
                disabled={disabled || currentUnits <= 1}
                aria-label="Decrease units"
                sx={{ p: 0.25 }}
              >
                <Remove sx={{ fontSize: '0.875rem' }} />
              </IconButton>
              <TextField
                value={currentUnits}
                onChange={e => {
                  const val = parseInt(e.target.value, 10);
                  if (!isNaN(val)) handleUnitsChange(index, val);
                }}
                variant="standard"
                size="small"
                disabled={disabled}
                inputProps={{
                  min: 1,
                  max: 99,
                  style: {
                    textAlign: 'center',
                    width: 28,
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                  },
                  'aria-label': `Units for code ${codes[index]}`,
                }}
                sx={{
                  '& .MuiInput-underline:before': { borderBottomStyle: 'dashed' },
                }}
              />
              <IconButton
                size="small"
                onClick={() => handleUnitsChange(index, currentUnits + 1)}
                disabled={disabled || currentUnits >= 99}
                aria-label="Increase units"
                sx={{ p: 0.25 }}
              >
                <Add sx={{ fontSize: '0.875rem' }} />
              </IconButton>
            </Box>
          </Stack>

          {/* Diagnosis code pointers */}
          {claimDiagnosisCodes.length > 0 && (
            <Box sx={{ mt: 1.5, pt: 1, borderTop: `1px dashed ${theme.palette.divider}` }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                Linked diagnoses
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {(() => {
                  const lineDx = lineItems?.[index]?.diagnosis_codes ?? [];
                  const allApply = lineDx.length === 0;
                  return claimDiagnosisCodes.map(dx => {
                    const isLinked = allApply || lineDx.includes(dx);
                    return (
                      <Chip
                        key={dx}
                        label={dx}
                        size="small"
                        variant={isLinked ? 'filled' : 'outlined'}
                        color={isLinked ? 'primary' : 'default'}
                        onClick={() => {
                          if (disabled) return;
                          if (allApply) {
                            // Switching from "all" to explicit: unlink clicked, keep the rest
                            handleDiagnosisCodesChange(index, claimDiagnosisCodes.filter(d => d !== dx));
                          } else if (isLinked) {
                            const remaining = lineDx.filter(d => d !== dx);
                            // If removing last one, go back to "all apply"
                            handleDiagnosisCodesChange(index, remaining);
                          } else {
                            handleDiagnosisCodesChange(index, [...lineDx, dx]);
                          }
                        }}
                        disabled={disabled}
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          height: 22,
                          cursor: disabled ? 'default' : 'pointer',
                        }}
                      />
                    );
                  });
                })()}
              </Box>
              {(() => {
                const lineDx = lineItems?.[index]?.diagnosis_codes ?? [];
                return lineDx.length === 0 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontSize: '0.65rem', fontStyle: 'italic' }}>
                    All diagnoses apply. Click one to link specific codes.
                  </Typography>
                ) : null;
              })()}
            </Box>
          )}
        </Box>
      </Collapse>
    );
  }, [expandedSlot, showModifiers, codes, lineItems, handleModifiersChange, handleUnitsChange, handleDiagnosisCodesChange, claimDiagnosisCodes, disabled, theme]);

  // Filter options based on input
  const filterOptions = (options: CPTCodeOption[], state: { inputValue: string }) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return options;

    return options.filter(
      opt =>
        opt.cpt.includes(input) ||
        opt.description.toLowerCase().includes(input) ||
        opt.category.toLowerCase().includes(input),
    );
  };

  return (
    <Box>
      {showHeader && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            Enter up to two CPT codes for this encounter (optional).
          </Typography>
          <Tooltip title={CPT_INFO_TOOLTIP} placement="top" arrow>
            <InfoOutlined
              sx={{
                fontSize: '1rem',
                color: 'text.secondary',
                cursor: 'help',
              }}
            />
          </Tooltip>
        </Box>
      )}

      {showHelperText && (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 1.5 }}>
          This information will help JotPsych audit your note to ensure reimbursement.
        </Typography>
      )}

      <Stack direction="row" spacing={2}>
        <Box sx={{ flex: 1 }}>
          <Autocomplete
            value={primaryValue}
            inputValue={primaryInput}
            onInputChange={(_event, newInputValue, reason) => {
              if (reason === 'clear') {
                setPrimaryInput('');
                const newCodes = [...codes];
                newCodes[0] = '';
                emitChange(newCodes);
              } else if (reason === 'input') {
                setPrimaryInput(newInputValue.replace(/\D/g, '').slice(0, 5));
              } else if (reason === 'reset') {
                setPrimaryInput(newInputValue);
              }
            }}
            onChange={handlePrimaryChange}
            onBlur={() => {
              if (primaryInput !== codes[0]) {
                const newCodes = [...codes];
                newCodes[0] = primaryInput;
                emitChange(newCodes);
              }
            }}
            options={PRIMARY_CPT_CODES}
            groupBy={option => option.category}
            getOptionLabel={option => (typeof option === 'string' ? option : option.cpt)}
            renderOption={renderOption}
            filterOptions={filterOptions}
            freeSolo
            fullWidth
            size={size}
            disabled={disabled}
            renderInput={params => (
              <TextField
                {...params}
                label="Primary code"
                placeholder="e.g., 90834"
                onKeyDown={handleKeyDown}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 5,
                }}
              />
            )}
            ListboxProps={{
              sx: { maxHeight: 300 },
            }}
          />
          {renderModifiersToggle(0)}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Autocomplete
            value={addonValue}
            inputValue={addonInput}
            onInputChange={(_event, newInputValue, reason) => {
              if (reason === 'clear') {
                setAddonInput('');
                const newCodes = [...codes];
                newCodes[1] = '';
                emitChange(newCodes);
              } else if (reason === 'input') {
                setAddonInput(newInputValue.replace(/\D/g, '').slice(0, 5));
              } else if (reason === 'reset') {
                setAddonInput(newInputValue);
              }
            }}
            onChange={handleAddonChange}
            onBlur={() => {
              if (addonInput !== codes[1]) {
                const newCodes = [...codes];
                newCodes[1] = addonInput;
                emitChange(newCodes);
              }
            }}
            options={ADDON_CPT_CODES}
            getOptionLabel={option => (typeof option === 'string' ? option : option.cpt)}
            renderOption={renderOption}
            filterOptions={filterOptions}
            freeSolo
            fullWidth
            size={size}
            disabled={disabled}
            renderInput={params => (
              <TextField
                {...params}
                label="Add-on code"
                placeholder="e.g., 90833"
                onKeyDown={handleKeyDown}
                inputProps={{
                  ...params.inputProps,
                  maxLength: 5,
                }}
              />
            )}
            ListboxProps={{
              sx: { maxHeight: 300 },
            }}
          />
          {renderModifiersToggle(1)}
        </Box>
      </Stack>

      {/* Full-width expanded modifiers & units area */}
      {renderExpandedModifiersAndUnits()}

      {/* Use Suggested Codes Button - show when suggested codes exist and current value is empty */}
      {suggestedCodes &&
        suggestedCodes.length > 0 &&
        suggestedCodes.some(c => c && c.trim() !== '') &&
        onUseSuggestedCodes &&
        (!value || value.length === 0 || value.every(c => !c || c.trim() === '')) && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                JotPsych suggests these codes based on your note content:
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
              {suggestedCodes
                .filter(c => c && c.trim() !== '')
                .map((code, idx) => (
                  <Chip
                    key={idx}
                    label={code}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontFamily: 'monospace',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      opacity: 0.7,
                      borderStyle: 'dashed',
                      borderColor: theme.palette.text.disabled,
                      color: theme.palette.text.secondary,
                    }}
                  />
                ))}
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={onUseSuggestedCodes}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                '&:hover': {
                  bgcolor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              Use Suggested Codes
            </Button>
          </Box>
        )}
    </Box>
  );
};
